import { useEffect, useState } from 'react';
import { supabase } from './supabase';

/**
 * ai_jobs.card_id is Trello's 24-char card id, and trello.com/c/<that id>
 * does not open the card. The card's working link (its shortLink URL) is
 * stored on comms_items.card_url, so read it from there.
 */
export async function fetchTrelloCardUrl(cardId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('comms_items')
    .select('card_url')
    .eq('card_id', cardId)
    .like('card_url', 'https://trello.com/c/%')
    .limit(1)
    .maybeSingle();
  if (error) return null;
  return (data as { card_url: string } | null)?.card_url ?? null;
}

/** Card link for a job, or null while loading / when no comm row has one. */
export function useTrelloCardUrl(cardId: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    setUrl(null);
    if (!cardId) return;
    let alive = true;
    void fetchTrelloCardUrl(cardId).then((u) => {
      if (alive) setUrl(u);
    });
    return () => {
      alive = false;
    };
  }, [cardId]);
  return url;
}

/** The client's active Client Comms board, as a trello.com/b link, or null. */
export async function fetchTrelloBoardUrl(clientId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('comms_boards')
    .select('trello_board_id')
    .eq('client_id', clientId)
    .eq('is_active', true)
    .limit(1)
    .maybeSingle();
  if (error) return null;
  const boardId = (data as { trello_board_id: string } | null)?.trello_board_id;
  return boardId ? `https://trello.com/b/${boardId}` : null;
}

export type TrelloLink = { url: string; kind: 'card' | 'board' };

/**
 * Where the client note goes: the job's card, or, when the job has no card
 * (or no comm row carries its link), the client's board, so the note still
 * has a place to land. Null while loading or when the client has no board.
 */
export function useTrelloLink(
  cardId: string | null | undefined,
  clientId: string | null | undefined,
): TrelloLink | null {
  const [link, setLink] = useState<TrelloLink | null>(null);
  useEffect(() => {
    setLink(null);
    if (!cardId && !clientId) return;
    let alive = true;
    void (async () => {
      const cardUrl = cardId ? await fetchTrelloCardUrl(cardId) : null;
      if (cardUrl) {
        if (alive) setLink({ url: cardUrl, kind: 'card' });
        return;
      }
      const boardUrl = clientId ? await fetchTrelloBoardUrl(clientId) : null;
      if (alive && boardUrl) setLink({ url: boardUrl, kind: 'board' });
    })();
    return () => {
      alive = false;
    };
  }, [cardId, clientId]);
  return link;
}
